import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Filter, Download, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import SearchResults from '../components/SearchResults';
import searchService, { SearchFilters, SearchParams } from '../services/searchService';
import { normalizeLegalFormForFilter } from '../utils/legalFormUtils';
import { trackExport } from '../utils/analytics';

const SearchPage: React.FC = () => {
    const [urlSearchParams, setUrlSearchParams] = useSearchParams();
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState<SearchFilters>({
        country_code: '',
        legal_form: '',
        lou: ''
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [searchParams, setSearchParams] = useState<SearchParams | null>(null);
    const [searchType, setSearchType] = useState<'exact' | 'fuzzy'>('exact');
    const [searchFields, setSearchFields] = useState<string[]>([
        'registered_name',
        'transliterated_name',
        'lei'
    ]);
    const [showAdvanced, setShowAdvanced] = useState(false);

    const availableFields = [
        { value: 'registered_name', label: 'Registered Name' },
        { value: 'transliterated_name', label: 'Transliterated Registered Name' },
        { value: 'lei', label: 'Legal Entity Identifier' },
        { value: 'legal_address', label: 'Business Address' },
        { value: 'headquarters_address', label: 'Headquarters Address' }
    ];

    // Initialize state from URL parameters on mount
    useEffect(() => {
        const query = urlSearchParams.get('q') || '';
        const country = urlSearchParams.get('country') || '';
        const legalForm = urlSearchParams.get('legal_form') || '';
        const lou = urlSearchParams.get('lou') || '';
        const searchTypeParam =
            (urlSearchParams.get('search_type') as 'exact' | 'fuzzy') || 'exact';
        const fieldsParam = urlSearchParams.get('fields') || '';
        const page = parseInt(urlSearchParams.get('page') || '1', 10);

        setSearchQuery(query);
        setSearchType(searchTypeParam);
        setSearchFields(
            fieldsParam ? fieldsParam.split(',') : ['registered_name', 'transliterated_name', 'lei']
        );
        setFilters({
            country_code: country,
            legal_form: legalForm,
            lou: lou
        });
        setCurrentPage(page);

        // If there's a query in the URL, set up search params to trigger the search
        if (query.trim()) {
            setSearchParams({
                query: query.trim(),
                searchType: searchTypeParam,
                searchFields: fieldsParam
                    ? fieldsParam.split(',')
                    : ['registered_name', 'transliterated_name', 'lei'],
                filters: {
                    country_code: country,
                    legal_form: legalForm,
                    lou: lou
                },
                page,
                size: 20
            });
        }
    }, [urlSearchParams]);

    // Fetch facets for filter options (fallback when no search results)
    const { data: staticFacets } = useQuery({
        queryKey: ['facets'],
        queryFn: () => searchService.getFacets(),
        staleTime: 5 * 60 * 1000 // 5 minutes
    });

    // Search query
    const {
        data: searchResults,
        isLoading: searchLoading,
        error: searchError
    } = useQuery({
        queryKey: ['search', searchParams],
        queryFn: () => searchService.search(searchParams!),
        enabled: !!searchParams,
        staleTime: 10 * 60 * 1000, // 10 minutes - longer to preserve results
        gcTime: 30 * 60 * 1000 // 30 minutes - keep in cache longer
    });

    // Use dynamic facets from search results when available, otherwise use static facets
    const rawFacets = searchResults?.facets || staticFacets;

    // Process legal form facets to merge variants and normalize to sentence case
    const processLegalFormFacets = (legalForms: Array<{ key: string; count: number }>) => {
        if (!legalForms) return [];

        const merged = new Map<string, number>();

        legalForms.forEach((form) => {
            const normalizedKey = normalizeLegalFormForFilter(form.key);
            const existingCount = merged.get(normalizedKey) || 0;
            merged.set(normalizedKey, existingCount + form.count);
        });

        return Array.from(merged.entries()).map(([key, count]) => ({ key, count }));
    };

    // Process facets with normalized legal forms
    const facets = rawFacets
        ? {
              ...rawFacets,
              legal_forms: processLegalFormFacets(rawFacets.legal_forms)
          }
        : null;

    // Handle search submission
    const handleSearch = useCallback(() => {
        if (searchQuery.trim()) {
            setCurrentPage(1);
            const newSearchParams = {
                query: searchQuery.trim(),
                filters,
                searchType,
                searchFields,
                page: 1,
                size: 20
            };
            setSearchParams(newSearchParams);

            // Update URL parameters
            const newUrlParams = new URLSearchParams();
            newUrlParams.set('q', searchQuery.trim());
            if (filters.country_code) newUrlParams.set('country', filters.country_code);
            if (filters.legal_form) newUrlParams.set('legal_form', filters.legal_form);
            if (filters.lou) newUrlParams.set('lou', filters.lou);
            newUrlParams.set('search_type', searchType);
            newUrlParams.set('fields', searchFields.join(','));
            newUrlParams.set('page', '1');

            setUrlSearchParams(newUrlParams);
        }
    }, [searchQuery, filters, searchType, searchFields, setUrlSearchParams]);

    // Handle Enter key in search input
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // Handle filter changes
    const handleFilterChange = (filterName: keyof SearchFilters, value: string) => {
        const newFilters = { ...filters, [filterName]: value };
        setFilters(newFilters);

        // Auto-apply the filter by triggering a search
        if (searchQuery.trim()) {
            setCurrentPage(1);
            const newSearchParams = {
                query: searchQuery,
                searchType,
                searchFields,
                filters: newFilters,
                page: 1,
                size: 20
            };
            setSearchParams(newSearchParams);

            // Update URL parameters
            const newUrlParams = new URLSearchParams();
            newUrlParams.set('q', searchQuery);
            newUrlParams.set('search_type', searchType);
            newUrlParams.set('search_fields', searchFields.join(','));
            if (newFilters.country_code) newUrlParams.set('country', newFilters.country_code);
            if (newFilters.legal_form) newUrlParams.set('legal_form', newFilters.legal_form);
            if (newFilters.lou) newUrlParams.set('lou', newFilters.lou);
            newUrlParams.set('page', '1');
            setUrlSearchParams(newUrlParams);
        }
    };

    // Handle search field selection
    const handleFieldToggle = (fieldValue: string) => {
        setSearchFields((prev) =>
            prev.includes(fieldValue) ? prev.filter((f) => f !== fieldValue) : [...prev, fieldValue]
        );
    };

    // Handle clearing all filters
    const handleClearFilters = () => {
        const clearedFilters = { country_code: '', legal_form: '', lou: '' };
        setFilters(clearedFilters);

        // Auto-apply the cleared filters by triggering a search
        if (searchQuery.trim()) {
            setCurrentPage(1);
            const newSearchParams = {
                query: searchQuery,
                searchType,
                searchFields,
                filters: clearedFilters,
                page: 1,
                size: 20
            };
            setSearchParams(newSearchParams);

            // Update URL parameters
            const newUrlParams = new URLSearchParams();
            newUrlParams.set('q', searchQuery);
            newUrlParams.set('search_type', searchType);
            newUrlParams.set('search_fields', searchFields.join(','));
            newUrlParams.set('page', '1');
            setUrlSearchParams(newUrlParams);
        }
    };

    // Handle page changes
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        if (searchParams) {
            const newSearchParams = { ...searchParams, page };
            setSearchParams(newSearchParams);

            // Update URL parameters
            const newUrlParams = new URLSearchParams(urlSearchParams);
            newUrlParams.set('page', page.toString());
            setUrlSearchParams(newUrlParams);
        }
    };

    // Handle export
    const handleExport = async (format: 'csv' | 'json') => {
        if (!searchParams) return;

        try {
            const blob = await searchService.exportResults(searchParams, format);

            // Track export event
            trackExport(format);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `lei_search_results.${format}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Export failed:', error);
            alert('Export failed. Please try again.');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Page Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Search LEI Records</h1>
                    <p className="text-gray-600">
                        Search through millions of Legal Entity Identifier records with advanced
                        filtering options.
                    </p>
                </div>

                {/* Search Form */}
                <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-6 mb-8">
                    <div className="flex flex-col lg:flex-row gap-4">
                        {/* Search Input */}
                        <div className="flex-1">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                                <input
                                    type="text"
                                    placeholder="Search by LEI, entity name, or other identifiers..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        {/* Search Button */}
                        <button
                            onClick={handleSearch}
                            disabled={!searchQuery.trim() || searchLoading}
                            className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {searchLoading ? 'Searching...' : 'Search'}
                        </button>

                        {/* Advanced Search Toggle */}
                        <button
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            className="border border-gray-300 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                        >
                            Advanced
                        </button>
                    </div>

                    {/* Advanced Search Options */}
                    {showAdvanced && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Search Type */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-3">
                                        Search Type
                                    </label>
                                    <div className="space-y-2">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                value="fuzzy"
                                                checked={searchType === 'fuzzy'}
                                                onChange={(e) =>
                                                    setSearchType(e.target.value as 'fuzzy')
                                                }
                                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                                            />
                                            <span className="ml-2 text-sm text-gray-900">
                                                Fuzzy - finds similar matches
                                            </span>
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                value="exact"
                                                checked={searchType === 'exact'}
                                                onChange={(e) =>
                                                    setSearchType(e.target.value as 'exact')
                                                }
                                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                                            />
                                            <span className="ml-2 text-sm text-gray-900">
                                                Exact (default) - matches exact phrases only
                                            </span>
                                        </label>
                                    </div>
                                </div>

                                {/* Search Fields */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-3">
                                        Search Fields (leave empty to search all)
                                    </label>
                                    <div className="space-y-2">
                                        {availableFields.map((field) => (
                                            <label key={field.value} className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    value={field.value}
                                                    checked={searchFields.includes(field.value)}
                                                    onChange={() => handleFieldToggle(field.value)}
                                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded-sm"
                                                />
                                                <span className="ml-2 text-sm text-gray-900">
                                                    {field.label}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Advanced Filters */}
                    <div className="mt-6 pt-6 border-t border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Filter className="h-5 w-5 text-gray-500" />
                                <h3 className="text-lg font-medium text-gray-900">
                                    Advanced Filters
                                </h3>
                            </div>
                            <button
                                onClick={handleClearFilters}
                                className="text-sm text-primary-600 hover:text-primary-800 underline"
                                disabled={
                                    !filters.country_code && !filters.legal_form && !filters.lou
                                }
                            >
                                Clear Filters
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Country
                                </label>
                                <select
                                    value={filters.country_code}
                                    onChange={(e) =>
                                        handleFilterChange('country_code', e.target.value)
                                    }
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="">All Countries</option>
                                    {facets?.country_codes
                                        ?.sort((a, b) => a.key.localeCompare(b.key))
                                        ?.map((country: { key: string; count: number }) => (
                                            <option key={country.key} value={country.key}>
                                                {country.key} ({country.count.toLocaleString()})
                                            </option>
                                        )) || (
                                        <>
                                            <option value="US">United States</option>
                                            <option value="GB">United Kingdom</option>
                                            <option value="DE">Germany</option>
                                            <option value="FR">France</option>
                                            <option value="JP">Japan</option>
                                        </>
                                    )}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Legal Form
                                </label>
                                <select
                                    value={filters.legal_form}
                                    onChange={(e) =>
                                        handleFilterChange('legal_form', e.target.value)
                                    }
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="">All Legal Forms</option>
                                    {facets?.legal_forms
                                        ?.sort((a, b) => a.key.localeCompare(b.key))
                                        ?.map((form: { key: string; count: number }) => (
                                            <option key={form.key} value={form.key}>
                                                {form.key} ({form.count.toLocaleString()})
                                            </option>
                                        )) || (
                                        <>
                                            <option value="LLC">Limited Liability Company</option>
                                            <option value="CORP">Corporation</option>
                                            <option value="PART">Partnership</option>
                                            <option value="TRUST">Trust</option>
                                        </>
                                    )}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    LOU
                                </label>
                                <select
                                    value={filters.lou}
                                    onChange={(e) => handleFilterChange('lou', e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="">All LOUs</option>
                                    {facets?.lous
                                        ?.sort((a, b) => a.key.localeCompare(b.key))
                                        ?.map((lou: { key: string; count: number }) => (
                                            <option key={lou.key} value={lou.key}>
                                                {lou.key} ({lou.count.toLocaleString()})
                                            </option>
                                        )) || (
                                        <>
                                            <option value="GLEIF">GLEIF</option>
                                            <option value="DTCC">DTCC</option>
                                            <option value="WM">WM Datenservice</option>
                                        </>
                                    )}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Error Display */}
                {searchError && (
                    <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                            <AlertCircle className="h-5 w-5 text-red-400" />
                            <div>
                                <h3 className="text-sm font-medium text-red-800">Search Error</h3>
                                <p className="text-sm text-red-700 mt-1">
                                    {searchError instanceof Error
                                        ? searchError.message
                                        : 'An error occurred during search'}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Search Results */}
                {searchParams && (
                    <div className="mb-8">
                        <SearchResults
                            records={searchResults?.results || []}
                            total={searchResults?.total || 0}
                            page={currentPage}
                            size={20}
                            onPageChange={handlePageChange}
                            loading={searchLoading}
                        />
                    </div>
                )}

                {/* Initial State */}
                {!searchParams && !searchLoading && (
                    <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-6">
                        <div className="text-center py-12">
                            <Search className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                No search results yet
                            </h3>
                            <p className="text-gray-500">
                                Enter your search criteria above to find LEI records.
                            </p>
                        </div>
                    </div>
                )}

                {/* Export Options */}
                {searchResults && searchResults.results.length > 0 && (
                    <div className="mt-8 bg-white rounded-lg shadow-xs border border-gray-200 p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900">
                                    Export Results
                                </h3>
                                <p className="text-gray-600">
                                    Download your search results in various formats
                                </p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={() => handleExport('csv')}
                                    className="flex items-center justify-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                                >
                                    <Download className="h-4 w-4" />
                                    CSV Export
                                </button>
                                <button
                                    onClick={() => handleExport('json')}
                                    className="flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    <Download className="h-4 w-4" />
                                    JSON Export
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchPage;
